import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/**
 * cca2.js — Commercial Growth System: Commercial Clients page
 * Content sourced 1:1 from "COMMERCIAL GROWTH SYSTEM — Private Invitation"
 *
 * Drop-in page component for a MERN app.
 * No external UI libraries required — styles are scoped in the injected
 * <style> tag below (id="cca2-styles"), so this file is self-contained.
 *
 * Usage (React Router example):
 *   import Cca2 from "./pages/cca2";
 *   <Route path="/commercial-clients" element={<Cca2 />} />
 */

const PLANS = [
  {
    tag: "TEST PILOT LAUNCHPAD",
    price: "$47",
    delivery: "One-Time · 7-Day Delivery",
    desc: "The no-brainer proof of concept. See real results in your area before committing to anything bigger.",
    features: [
      "5 Booked Appointments",
      "Custom Coded Website",
      "Offer & Pricing Makeover",
      "500 Verified Local Leads",
      "Social Media Management",
      "SEO & Backlinking Setup",
    ],
    featured: false,
  },
  {
    tag: "GROWTH ENGINE PIPELINE",
    price: "$197",
    delivery: "One-Time · 14-Day Delivery",
    desc: "The guaranteed ROI machine. We promise 1 closed sale or we work free until you get one.",
    features: [
      "25 Booked Appointments",
      "Everything in LaunchPad",
      "Omnipresence Outreach",
      "Automated Follow-Ups",
      "Sales & Closing Coaching",
      "1 Closed Sale Guaranteed",
    ],
    featured: true,
  },
  {
    tag: "FULL SYSTEM DOMINATOR",
    price: "$450",
    delivery: "One-Time · 30-Day Delivery",
    desc: "Total market domination. Built to land recurring commercial contracts and lock your city.",
    features: [
      "50 Booked Appointments",
      "Everything in Pipeline",
      "Complete CRM Setup",
      "Review Generation Machine",
      "Advanced Retargeting",
      "2,000+ Verified Leads Vault",
    ],
    featured: false,
  },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("cca2-in");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("cca2-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

export default function Cca2() {
  const heroRef = useReveal();
  const plansRef = useReveal();
  const bottomRef = useReveal();

  return (
    <div className="cca2-root">
      <style id="cca2-styles">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        /* ===== ROOT ===== */
        .cca2-root {
          background: #ffffff;
          color: #111111;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
          line-height: 1.6;
          overflow-x: hidden;
        }
        .cca2-root * { box-sizing: border-box; margin: 0; padding: 0; }

        /* ===== REVEAL ===== */
        .cca2-reveal {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.65s ease, transform 0.65s cubic-bezier(0.2, 0.7, 0.3, 1);
        }
        .cca2-reveal.cca2-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* ===== WRAPPER ===== */
        .cca2-wrap {
          max-width: 900px;
          margin: 0 auto;
          padding: 0 40px;
        }

        /* ===== TOP HEADER BAR ===== */
        .cca2-header {
          max-width: 900px;
          margin: 0 auto;
          padding: 20px 40px 18px;
          border-bottom: 1px solid #111111;
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #111111;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .cca2-header-label { font-weight: 400; }
        .cca2-header-bold { font-weight: 700; }

        /* ===== HERO ===== */
        .cca2-hero {
          padding: 64px 0 56px;
        }
        .cca2-from-desk {
          font-size: 14px;
          font-style: italic;
          color: #555555;
          margin-bottom: 20px;
        }
        .cca2-headline {
          font-size: clamp(30px, 5.5vw, 56px);
          font-weight: 800;
          line-height: 1.08;
          letter-spacing: -0.02em;
          color: #111111;
          max-width: 20ch;
          margin-bottom: 32px;
        }
        .cca2-body-p {
          font-size: 15px;
          line-height: 1.75;
          color: #333333;
          max-width: 72ch;
          margin-bottom: 16px;
        }
        .cca2-body-p:last-of-type { margin-bottom: 0; }
        .cca2-body-p strong { font-weight: 700; color: #111111; }
        .cca2-bold-line {
          font-size: 15px;
          font-weight: 700;
          color: #111111;
          margin-top: 20px;
          max-width: 72ch;
        }

        /* ===== PLANS GRID ===== */
        .cca2-plans-section {
          padding: 0 0 64px;
        }
        .cca2-plans-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0;
          border: 1px solid #d8d8d8;
        }

        /* ===== PLAN CARD ===== */
        .cca2-card {
          padding: 28px 24px 32px;
          border-right: 1px solid #d8d8d8;
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        .cca2-card:last-child { border-right: none; }

        .cca2-card--featured {
          background: #1a2b4a;
          color: #ffffff;
          border-right: none;
        }

        .cca2-card-tag {
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #555555;
          margin-bottom: 12px;
        }
        .cca2-card--featured .cca2-card-tag {
          color: #8aafd4;
        }

        .cca2-card-price {
          font-size: clamp(40px, 5vw, 52px);
          font-weight: 900;
          line-height: 1;
          letter-spacing: -0.03em;
          color: #111111;
          margin-bottom: 6px;
        }
        .cca2-card--featured .cca2-card-price { color: #ffffff; }

        .cca2-card-delivery {
          font-size: 12px;
          color: #777777;
          margin-bottom: 16px;
        }
        .cca2-card--featured .cca2-card-delivery { color: #8aafd4; }

        .cca2-card-divider {
          height: 1px;
          background: #e0e0e0;
          margin-bottom: 16px;
        }
        .cca2-card--featured .cca2-card-divider { background: rgba(255,255,255,0.15); }

        .cca2-card-desc {
          font-size: 13.5px;
          line-height: 1.65;
          color: #444444;
          margin-bottom: 20px;
        }
        .cca2-card--featured .cca2-card-desc { color: #c8ddf0; }

        .cca2-card-features {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: auto;
        }
        .cca2-card-feature {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          font-size: 13.5px;
          color: #222222;
          line-height: 1.4;
        }
        .cca2-card--featured .cca2-card-feature { color: #e8f2fb; }

        .cca2-arrow {
          color: #1a2b4a;
          font-weight: 700;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .cca2-card--featured .cca2-arrow { color: #6ab0e8; }

        .cca2-feature-bold {
          font-weight: 700;
        }

        /* ===== BOTTOM SECTION ===== */
        .cca2-bottom {
          padding: 48px 0 72px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0 56px;
          border-top: 1px solid #e0e0e0;
        }

        /* Guarantee */
        .cca2-guarantee {
          display: flex;
          gap: 18px;
          align-items: flex-start;
        }
        .cca2-shield {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          margin-top: 2px;
        }
        .cca2-guarantee-title {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #111111;
          margin-bottom: 10px;
        }
        .cca2-guarantee-body {
          font-size: 14px;
          line-height: 1.7;
          color: #444444;
        }

        /* Territory */
        .cca2-territory {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .cca2-territory-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #cc2200;
        }
        .cca2-territory-label svg { flex-shrink: 0; }
        .cca2-territory-body {
          font-size: 14px;
          line-height: 1.7;
          color: #444444;
        }
        .cca2-cta-btn {
          display: inline-block;
          margin-top: 6px;
          background: #1a2b4a;
          color: #ffffff;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 14px 24px;
          border: none;
          cursor: pointer;
          text-decoration: none;
          transition: background 0.2s ease, transform 0.2s ease;
          align-self: flex-start;
        }
        .cca2-cta-btn:hover {
          background: #243d6a;
          transform: translateY(-1px);
        }
        .cca2-cta-btn:focus-visible {
          outline: 2px solid #1a2b4a;
          outline-offset: 3px;
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 760px) {
          .cca2-wrap { padding: 0 20px; }
          .cca2-header { padding: 18px 20px 16px; }

          .cca2-plans-grid {
            grid-template-columns: 1fr;
          }
          .cca2-card {
            border-right: none;
            border-bottom: 1px solid #d8d8d8;
          }
          .cca2-card:last-child { border-bottom: none; }

          .cca2-bottom {
            grid-template-columns: 1fr;
            gap: 40px 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .cca2-reveal {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>

      {/* Header */}
      <div className="cca2-header">
        <span className="cca2-header-label">COMMERCIAL GROWTH SYSTEM</span>
        <span className="cca2-header-bold">Private Invitation</span>
      </div>

      {/* Hero */}
      <div className="cca2-wrap">
        <section className="cca2-hero cca2-reveal" ref={heroRef}>
          <p className="cca2-from-desk">From the desk of your new growth partner.</p>
          <h1 className="cca2-headline">
            If you can handle the volume, I will build you a machine that never sleeps.
          </h1>
          <p className="cca2-body-p">
            Right now, 500 businesses in your city need exactly what you sell. But they
            don't know you exist. You are losing deals not because your product is worse
            — but because your competitors are louder, more visible, and better
            positioned. That ends today.
          </p>
          <p className="cca2-body-p">
            We will build your custom website, fix your pricing so prospects feel stupid
            saying no, manage your social media, run your SEO, hand you 500 verified
            local leads, and{" "}
            <strong>
              book real commercial appointments directly into your calendar.
            </strong>
          </p>
          <p className="cca2-bold-line">
            You do no prospecting. You do no cold calling. You just show up and close.
          </p>
        </section>

        {/* Plans */}
        <section className="cca2-plans-section cca2-reveal" ref={plansRef}>
          <div className="cca2-plans-grid">
            {PLANS.map((plan) => (
              <div
                key={plan.tag}
                className={`cca2-card${plan.featured ? " cca2-card--featured" : ""}`}
              >
                <div className="cca2-card-tag">{plan.tag}</div>
                <div className="cca2-card-price">{plan.price}</div>
                <div className="cca2-card-delivery">{plan.delivery}</div>
                <div className="cca2-card-divider" />
                <p className="cca2-card-desc">{plan.desc}</p>
                <ul className="cca2-card-features">
                  {plan.features.map((f, i) => {
                    const isLast = plan.featured && i === plan.features.length - 1;
                    return (
                      <li key={f} className="cca2-card-feature">
                        <span className="cca2-arrow">→</span>
                        <span className={isLast ? "cca2-feature-bold" : ""}>{f}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Bottom: Guarantee + Territory */}
        <section className="cca2-bottom cca2-reveal" ref={bottomRef}>
          {/* Ironclad Guarantee */}
          <div className="cca2-guarantee">
            <svg
              className="cca2-shield"
              viewBox="0 0 36 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M18 2L4 8v12c0 8.284 5.946 16.02 14 18 8.054-1.98 14-9.716 14-18V8L18 2z"
                stroke="#111111"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M12 20l4 4 8-8"
                stroke="#111111"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div>
              <div className="cca2-guarantee-title">The Ironclad Guarantee</div>
              <p className="cca2-guarantee-body">
                On the $197 Pipeline plan, we guarantee to deliver at least 1 closed
                sale. If we fail, we keep working for free until you get one. All the
                risk is on us. None of it is on you.
              </p>
            </div>
          </div>

          {/* Strictly Limited Territory */}
          <div className="cca2-territory">
            <div className="cca2-territory-label">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M7 1L13 13H1L7 1z" fill="#cc2200" />
                <path d="M7 5.5v3" stroke="#ffffff" strokeWidth="1.2" strokeLinecap="round" />
                <circle cx="7" cy="10.5" r="0.7" fill="#ffffff" />
              </svg>
              Strictly Limited Territory
            </div>
            <p className="cca2-territory-body">
              We only accept 3 partners per city. Once your territory is locked, we
              refuse to work with your competitors. Do not wait for someone else to take
              your spot.
            </p>
            <Link to="/pricing" className="cca2-cta-btn">
              CLAIM YOUR TERRITORY →
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
